import React from 'react';
import { cleanStegaDataRecursively } from '@workspace/lib/stega-utils';

import {
  ContentCarouselBlock,
  FeatureSummary,
  PageContactSupport,
  ProductSummary,
  ProductType,
  ProductVariant,
  TextIconList,
  VideoImageRenderer,
  MasterTextBlock,
  ProductCarousel,
  MultiColumnBanner,
  DocumentBlock,
  HeroBannerCarousel,
  LogoWall,
  ProductSlideshow,
  AskGregSectionRenderer,
  HeadlineRenderer,
  QuoteRenderer,
  MilestoneRenderer,
  Gallery,
  IconTextColumnsBlock,
} from '.';

import { ImageGrid } from './ImagesGrid';
import { Newsletter } from './Newsletter';
import { ImageTextBlock } from './ImageTextBlock';
import { Applications } from './PageApplications';
import { MiddleBanner } from './MiddleBanner';
import { TextSection } from './TextSection';
import { ContactFormBlock } from './ContactFormBlock';

type BlocksType = {
  [key: string]: React.ComponentType<any>;
};

type BlockType = {
  _type: string;
  _key: string;
};

type BlockProps = {
  index: number;
  block: BlockType;
  pageId: string;
  pageType: string;
};

const Blocks: BlocksType = {
  textSection: TextSection,
  imageTextBlock: ImageTextBlock,
  documentBlock: DocumentBlock,
  gallery: Gallery,
  imageGrid: ImageGrid,
  productSummary: ProductSummary,
  applicationsBlock: Applications,
  contactSupportBlock: PageContactSupport,
  // heroBannerBlock: HeroBannerBlock,
  productVariantSection: ProductVariant,
  middleBanner: MiddleBanner,
  featureSummary: FeatureSummary,
  productType: ProductType,
  videoImageBlock: VideoImageRenderer,
  contentCarousel: ContentCarouselBlock,
  heroBannerSlideshow: HeroBannerCarousel,
  textIconList: TextIconList,
  masterTextBlock: MasterTextBlock,
  productSlideshow: ProductSlideshow,
  productCarousel: ProductCarousel,
  multiColumnBanner: MultiColumnBanner,
  headline: HeadlineRenderer,
  milestoneTimeline: MilestoneRenderer,
  quote: QuoteRenderer,
  logoWall: LogoWall,
  askGreg: AskGregSectionRenderer,
  newsletter: Newsletter,
  contactFormBlock: ContactFormBlock,
  iconTextColumnsBlock: IconTextColumnsBlock,
};

/**
 * Used by the <PageBuilder>, this component renders a the component that matches the block type.
 * Automatically applies stega cleaning to all block data for live preview compatibility.
 */
export default function BlockRenderer({ block, index }: BlockProps) {
  if (!block || !block._type) return null;

  const cleanBlock = cleanStegaDataRecursively(block);
  const blockType =
    typeof cleanBlock._type === 'string' ? (cleanBlock._type.split('.').pop() ?? cleanBlock._type) : cleanBlock._type;

  // Block does exist
  if (typeof Blocks[blockType] !== 'undefined') {
    return (
      <div key={cleanBlock._key}>
        {React.createElement(Blocks[blockType], {
          key: cleanBlock._key,
          block: cleanBlock,
          index: index,
        })}
      </div>
    );
  }
  // Block doesn't exist yet
  return React.createElement(
    () => (
      <div className="w-full bg-secondary-100 text-center text-secondary-50 p-20 rounded">
        A &ldquo;{cleanBlock._type}&rdquo; block hasn&apos;t been created
      </div>
    ),
    { key: cleanBlock._key },
  );
}
